// ════════════════════════════════════════════════════════════════════════════
// PAGE — KPI D'UNE PHASE DE PLAN D'ACTION (4 graphes Chart.js)
// Extrait de page-action-plans.ts (découpe god-module 2026-07).
// ════════════════════════════════════════════════════════════════════════════
//
// Contient la page `pages.actionPlanPhaseKpi` (#action-plan-kpi/:id/:phaseId) et
// les helpers d'affichage de la SANTÉ (pastilles couleur, calculées côté backend
// — source unique). `phaseKpiPastilles` est réexporté et consommé par l'arbre des
// phases dans page-action-plans.ts (seul point de couplage, uni-directionnel).
//
// Dépendances : globals ambiants (esc, t, api, setContent, setHeader, makeChart,
// fmtDate, registerPageDisposer, pages, window.Chart) déclarés dans core.ts ;
// logique pure du domaine depuis domain/action-plan.ts.

import { reevaluatedEnd as domainReevaluatedEnd, pct as domainPct } from '../domain/action-plan.js';

export type HealthColor = 'green' | 'orange' | 'red' | 'grey';

interface KpiThresholds {
  blockedOrangePct: number;
  progressOrangePct: number;
  reevalStableWeeks: number;
}

export interface PhaseHealth {
  blocked: HealthColor;
  progress: HealthColor;
  enddate: HealthColor;
  updated: HealthColor;
}

interface KpiPoint {
  snapshot_date: string;
  avancement_constate: number | null;
  avancement_theorique: number | null;
  ecart: number | null;
  nb_actions_total: number;
  nb_actions_blocked: number;
  nb_actions_updated: number;
  phase_date_end_target: string | null;
}

interface KpiResponse {
  phase: {
    id: number;
    code: string;
    title: string;
    date_start: string | null;
    date_end_target: string | null;
  };
  thresholds: KpiThresholds;
  health: PhaseHealth;
  history: KpiPoint[];
}

const _kpiCharts: Array<{ destroy(): void }> = [];

function _destroyKpiCharts(): void {
  while (_kpiCharts.length) {
    try {
      _kpiCharts.pop()?.destroy();
    } catch {
      /* déjà détruit */
    }
  }
}

/** Charge Chart.js (CDN UMD) si absent, puis exécute cb. makeChart gère le cas
 *  où le chargement échoue (affiche common.chartMissing). */
function _ensureChartJs(cb: () => void): void {
  if (window.Chart) {
    cb();
    return;
  }
  const s = document.createElement('script');
  s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';
  s.onload = (): void => cb();
  s.onerror = (): void => cb();
  document.head.appendChild(s);
}

pages.actionPlanPhaseKpi = async function (params: Record<string, string>) {
  const planId = parseInt(params['id'] || '0', 10);
  const phaseId = parseInt(params['phaseId'] || '0', 10);
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  let data: KpiResponse;
  try {
    data = (await api.getPhaseKpis(phaseId)) as KpiResponse;
  } catch (e: unknown) {
    setContent(
      `<div class="error">${esc(e instanceof Error ? e.message : t('common.error'))}</div>`,
    );
    return;
  }
  renderKpiPage(planId, data);
};

function renderKpiPage(planId: number, data: KpiResponse): void {
  _destroyKpiCharts();
  const ph = data.phase;
  setHeader(
    `<a href="#action-plans" style="text-decoration:none">${esc(t('actionPlans.title'))}</a>
     <span style="color:var(--text3)"> / </span>
     <a href="#action-plan/${planId}" style="text-decoration:none">${esc(ph.code)} — ${esc(ph.title)}</a>
     <span style="color:var(--text3)"> / </span>${esc(t('actionPlans.kpi.title'))}`,
    '',
  );
  if (!data.history.length) {
    setContent(
      `<div class="card"><div class="empty" style="padding:40px;text-align:center;color:var(--text3)">${esc(t('actionPlans.kpi.empty'))}</div></div>`,
    );
    return;
  }
  // Pastilles santé (vert/orange/rouge) — calculées CÔTÉ BACKEND (source unique).
  const h = data.health;
  const blockedColor = h.blocked;
  const progressColor = h.progress;
  const endColor = h.enddate;
  const updatedColor = h.updated;

  const card = (titleKey: string, id: string, color: HealthColor): string =>
    `<div class="card" style="margin-bottom:16px">
       <h3 style="margin:0 0 10px 0;font-size:14px;display:flex;align-items:center">${pastille(color)}${esc(t(titleKey))}</h3>
       <div id="${id}" style="position:relative;height:300px"></div>
     </div>`;
  setContent(
    card('actionPlans.kpi.chart.blocked', 'kpi-chart-blocked', blockedColor) +
      card('actionPlans.kpi.chart.progress', 'kpi-chart-progress', progressColor) +
      card('actionPlans.kpi.chart.enddate', 'kpi-chart-enddate', endColor) +
      card('actionPlans.kpi.chart.updated', 'kpi-chart-updated', updatedColor),
  );

  const labels = data.history.map((h) => fmtDate(h.snapshot_date));
  _ensureChartJs(() => {
    _renderBlockedChart(labels, data.history);
    _renderProgressChart(data.history, data.phase);
    _renderEndDateChart(labels, data.history, data.phase);
    _renderUpdatedChart(labels, data.history);
  });
  registerPageDisposer(_destroyKpiCharts);
}

/** Date ISO 'YYYY-MM-DD' → timestamp ms (minuit local), ou null. */
function _ts(d: string | null): number | null {
  return d ? new Date(`${d}T00:00:00`).getTime() : null;
}

const _HEALTH_BG: Record<HealthColor, string> = {
  green: '#16a34a',
  orange: '#d97706',
  red: '#dc2626',
  grey: '#9ca3af',
};

/** Pastille santé colorée (vert/orange/rouge/gris) avec libellé au survol.
 *  `titlePrefix` (nom du KPI) précède le statut dans le tooltip : « KPI — statut ». */
export function pastille(color: HealthColor, titlePrefix?: string): string {
  const status = t(`actionPlans.kpi.health.${color}`);
  const label = titlePrefix ? `${titlePrefix} — ${status}` : status;
  return `<span title="${esc(label)}" aria-label="${esc(label)}" style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${_HEALTH_BG[color]};margin-right:8px;flex-shrink:0"></span>`;
}

/** Les 4 pastilles KPI affichées dans l'en-tête d'une phase (santé calculée
 *  backend). Tooltip par pastille = « <nom KPI> — <statut> ». Pas de santé
 *  (phase non encore calculée) → 4 pastilles grises. */
export function phaseKpiPastilles(health: PhaseHealth | undefined): string {
  const h: PhaseHealth = health || {
    blocked: 'grey',
    progress: 'grey',
    enddate: 'grey',
    updated: 'grey',
  };
  const dots = (
    [
      ['blocked', h.blocked],
      ['progress', h.progress],
      ['enddate', h.enddate],
      ['updated', h.updated],
    ] as Array<[string, HealthColor]>
  )
    .map(([key, color]) => pastille(color, t(`actionPlans.kpi.chart.${key}`)))
    .join('');
  return `<span class="phase-kpi-pastilles" style="display:inline-flex;align-items:center;margin-left:10px">${dots}</span>`;
}

/** Date de fin RÉÉVALUÉE = la plus tardive enregistrée dans l'historique (ou la
 *  date cible courante de la phase à défaut). Cf. domain/action-plan. */
function _reevaluatedEnd(hist: KpiPoint[], phase: KpiResponse['phase']): string | null {
  return domainReevaluatedEnd(phase.date_end_target, hist);
}

function _mk(id: string, config: unknown): void {
  const el = document.getElementById(id);
  if (!el) return;
  const chart = makeChart(el, config);
  if (chart) _kpiCharts.push(chart);
}

// Graphe 1 : nb actions bloquées (barres, axe gauche) + % bloquées (ligne, axe droit).
function _renderBlockedChart(labels: string[], hist: KpiPoint[]): void {
  const counts = hist.map((h) => h.nb_actions_blocked);
  const pct = hist.map((h) => domainPct(h.nb_actions_blocked, h.nb_actions_total));
  // Axe gauche borné au max d'actions de la période (cf. demande).
  const maxTotal = Math.max(1, ...hist.map((h) => h.nb_actions_total));
  _mk('kpi-chart-blocked', {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: t('actionPlans.kpi.series.blockedCount'),
          data: counts,
          backgroundColor: 'rgba(220,38,38,.35)',
          borderColor: '#dc2626',
          borderWidth: 1,
          yAxisID: 'y',
          order: 2,
        },
        {
          label: t('actionPlans.kpi.series.blockedPct'),
          data: pct,
          type: 'line',
          borderColor: '#d97706',
          backgroundColor: '#d97706',
          tension: 0.2,
          yAxisID: 'y1',
          order: 1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        y: {
          beginAtZero: true,
          position: 'left',
          max: maxTotal,
          ticks: { precision: 0, stepSize: 1 },
          title: { display: true, text: t('actionPlans.kpi.series.blockedCount') },
        },
        y1: {
          beginAtZero: true,
          max: 100,
          position: 'right',
          grid: { drawOnChartArea: false },
          ticks: { callback: (v: unknown) => `${v}%` },
          title: { display: true, text: t('actionPlans.kpi.series.blockedPct') },
        },
      },
    },
  });
}

// Graphe 2 : % avancement constaté (ligne, jusqu'à aujourd'hui) + trajectoire
// théorique (droite 0 % au 1er jour → 100 % à la date de fin RÉÉVALUÉE).
// Axe X linéaire temporel borné à [début phase, fin réévaluée].
function _renderProgressChart(hist: KpiPoint[], phase: KpiResponse['phase']): void {
  const startTs = _ts(phase.date_start);
  const endTs = _ts(_reevaluatedEnd(hist, phase));
  const actual = hist
    .map((h) => ({ x: _ts(h.snapshot_date), y: h.avancement_constate }))
    .filter((p) => p.x != null);
  // Trajectoire théorique = 2 points (droite) jusqu'à la fin réévaluée.
  const theo =
    startTs != null && endTs != null
      ? [
          { x: startTs, y: 0 },
          { x: endTs, y: 100 },
        ]
      : [];
  _mk('kpi-chart-progress', {
    type: 'line',
    data: {
      datasets: [
        {
          label: t('actionPlans.kpi.series.actual'),
          data: actual,
          borderColor: '#147A57',
          backgroundColor: 'rgba(20,122,87,.15)',
          tension: 0.2,
          spanGaps: true,
        },
        {
          label: t('actionPlans.kpi.series.theoretical'),
          data: theo,
          borderColor: '#9ca3af',
          borderDash: [6, 4],
          pointRadius: 0,
          tension: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        tooltip: {
          callbacks: {
            // Titre du tooltip = la date (X), pas le timestamp brut.
            title: (items: Array<{ parsed?: { x?: number } }>) => {
              const x = items[0]?.parsed?.x;
              return typeof x === 'number' ? new Date(x).toLocaleDateString('fr-FR') : '';
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: startTs ?? undefined,
          max: endTs ?? undefined,
          ticks: {
            callback: (v: unknown) =>
              typeof v === 'number' ? new Date(v).toLocaleDateString('fr-FR') : '',
          },
        },
        y: {
          beginAtZero: true,
          max: 100,
          ticks: { callback: (v: unknown) => `${v}%` },
        },
      },
    },
  });
}

// Graphe 3 : date de fin cible de la phase, réévaluée jour après jour. L'axe Y
// (dates) démarre au 1er jour de la phase (cf. demande).
function _renderEndDateChart(
  labels: string[],
  hist: KpiPoint[],
  phase: KpiResponse['phase'],
): void {
  const startTs = _ts(phase.date_start);
  _mk('kpi-chart-enddate', {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: t('actionPlans.kpi.series.endDate'),
          data: hist.map((h) => _ts(h.phase_date_end_target)),
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37,99,235,.15)',
          stepped: true,
          spanGaps: true,
        },
        {
          // Droite de référence X = Y : à chaque date X, Y = cette même date.
          // L'intersection avec la courbe = jour où la fin prévue est atteinte.
          label: t('actionPlans.kpi.series.endDateRef'),
          data: hist.map((h) => _ts(h.snapshot_date)),
          borderColor: '#9ca3af',
          borderDash: [4, 4],
          pointRadius: 0,
          tension: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          callbacks: {
            // Valeur = la date de fin cible (Y), pas le timestamp brut.
            label: (item: { dataset?: { label?: string }; parsed?: { y?: number } }) => {
              const y = item.parsed?.y;
              const d = typeof y === 'number' ? new Date(y).toLocaleDateString('fr-FR') : '—';
              return `${item.dataset?.label ?? ''}: ${d}`;
            },
          },
        },
      },
      scales: {
        y: {
          min: startTs ?? undefined,
          ticks: {
            callback: (v: unknown) =>
              typeof v === 'number' ? new Date(v).toLocaleDateString('fr-FR') : '',
          },
        },
      },
    },
  });
}

// Graphe 4 : part (%) des actions mises à jour ce jour-là sur la période.
function _renderUpdatedChart(labels: string[], hist: KpiPoint[]): void {
  const pct = hist.map((h) => domainPct(h.nb_actions_updated, h.nb_actions_total));
  _mk('kpi-chart-updated', {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: t('actionPlans.kpi.series.updatedPct'),
          data: pct,
          borderColor: '#0ea5e9',
          backgroundColor: 'rgba(14,165,233,.15)',
          tension: 0.2,
          fill: true,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: { callback: (v: unknown) => `${v}%` },
        },
      },
    },
  });
}
